import AddIcon from "@mui/icons-material/Add";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    TextField,
    Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import {
    useCreatePaymentSchedule,
    useUpdatePaymentSchedule,
} from "../../../services/agreementsService";
import { formatDecimalNumber } from "../../../utils";

export function PaymentScheduleDialog({ open, onClose, schedule }) {
    const { t } = useTranslation();
    const isEdit = !!schedule;

    const [scheduleName, setScheduleName] = useState("");
    const [installments, setInstallments] = useState([
        { installment_order: 1, installment_percentage: "" },
    ]);

    useEffect(() => {
        if (open) {
            if (schedule) {
                setScheduleName(schedule.schedule_name);
                setInstallments(
                    schedule.installments.map((inst) => ({
                        installment_order: inst.installment_order,
                        installment_percentage: inst.installment_percentage,
                    })),
                );
            } else {
                setScheduleName("");
                setInstallments([{ installment_order: 1, installment_percentage: "" }]);
            }
        }
    }, [open, schedule]);

    const createMutation = useCreatePaymentSchedule({
        onSuccess: () => {
            toast.success(t("paymentSchedules.createSuccess"));
            onClose();
        },
        onError: () => {
            toast.error(t("paymentSchedules.createError"));
        },
    });

    const updateMutation = useUpdatePaymentSchedule({
        onSuccess: () => {
            toast.success(t("paymentSchedules.updateSuccess"));
            onClose();
        },
        onError: () => {
            toast.error(t("paymentSchedules.updateError"));
        },
    });

    const handleAddInstallment = () => {
        const newOrder = installments.length + 1;
        setInstallments([
            ...installments,
            { installment_order: newOrder, installment_percentage: "" },
        ]);
    };

    const handleRemoveInstallment = (index) => {
        if (installments.length === 1) {
            toast.error(t("paymentSchedules.atLeastOneInstallment"));
            return;
        }
        const newInstallments = installments.filter((_, i) => i !== index);
        setInstallments(
            newInstallments.map((inst, idx) => ({
                ...inst,
                installment_order: idx + 1,
            })),
        );
    };

    const handlePercentageChange = (index, value) => {
        const newInstallments = [...installments];
        newInstallments[index].installment_percentage = value;
        setInstallments(newInstallments);
    };

    const handleAutoFill = (index) => {
        const currentTotal = installments.reduce((sum, inst, i) => {
            if (i === index) {
                return sum;
            }
            const percentage =
                typeof inst.installment_percentage === "string"
                    ? Number.parseFloat(inst.installment_percentage) || 0
                    : inst.installment_percentage || 0;
            return sum + percentage;
        }, 0);

        const remaining = 100 - currentTotal;
        const newInstallments = [...installments];
        newInstallments[index].installment_percentage = Math.max(0, remaining).toFixed(2);
        setInstallments(newInstallments);
    };

    const calculateTotal = () => {
        return installments.reduce((sum, inst) => {
            const percentage =
                typeof inst.installment_percentage === "string"
                    ? Number.parseFloat(inst.installment_percentage) || 0
                    : inst.installment_percentage || 0;
            return sum + percentage;
        }, 0);
    };

    const handleSave = () => {
        if (!scheduleName.trim()) {
            toast.error(t("paymentSchedules.scheduleNameRequired"));
            return;
        }

        const total = calculateTotal();
        if (Math.abs(total - 100) > 0.01) {
            toast.error(t("paymentSchedules.totalMustBe100"));
            return;
        }

        const data = {
            schedule_name: scheduleName,
            installments: installments.map((inst) => ({
                installment_order: inst.installment_order,
                installment_percentage:
                    typeof inst.installment_percentage === "string"
                        ? Number.parseFloat(inst.installment_percentage)
                        : inst.installment_percentage,
            })),
        };

        if (isEdit && schedule) {
            updateMutation.mutate({ scheduleId: schedule.id, data });
        } else {
            createMutation.mutate(data);
        }
    };

    const total = calculateTotal();

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                {isEdit ? t("paymentSchedules.editSchedule") : t("paymentSchedules.addSchedule")}
            </DialogTitle>
            <DialogContent>
                <Box sx={{ pt: 2 }}>
                    <TextField
                        fullWidth
                        label={t("paymentSchedules.scheduleName")}
                        value={scheduleName}
                        onChange={(e) => setScheduleName(e.target.value)}
                        sx={{ mb: 3 }}
                    />

                    <Typography variant="h6" sx={{ mb: 2 }}>
                        {t("paymentSchedules.installments")}
                    </Typography>

                    {installments.map((installment, index) => (
                        <Box
                            key={index}
                            sx={{
                                display: "flex",
                                gap: 2,
                                mb: 2,
                                alignItems: "center",
                            }}
                        >
                            <Typography sx={{ minWidth: 80 }}>
                                {t("paymentSchedules.installment")} {installment.installment_order}
                            </Typography>
                            <TextField
                                type="number"
                                label={t("paymentSchedules.percentage")}
                                value={installment.installment_percentage}
                                onChange={(e) => handlePercentageChange(index, e.target.value)}
                                inputProps={{ min: 0, max: 100, step: 1 }}
                                sx={{ flex: 1 }}
                            />
                            <IconButton
                                onClick={() => handleAutoFill(index)}
                                color="primary"
                                title={t("paymentSchedules.autoFill")}
                            >
                                <AutoFixHighIcon />
                            </IconButton>
                            <IconButton
                                onClick={() => handleRemoveInstallment(index)}
                                color="error"
                                disabled={installments.length === 1}
                            >
                                <DeleteOutlinedIcon />
                            </IconButton>
                        </Box>
                    ))}

                    <Button startIcon={<AddIcon />} onClick={handleAddInstallment} sx={{ mb: 2 }}>
                        {t("paymentSchedules.addInstallment")}
                    </Button>

                    <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
                        <Box
                            sx={{
                                position: "relative",
                                p: 2,
                                border: total === 0 ? "2px solid" : "2px solid",
                                borderColor:
                                    Math.abs(total - 100) > 0.01 ? "error.light" : "success.light",
                                bgcolor: "background.paper",
                                borderRadius: 1,
                                minWidth: 200,
                                textAlign: "center",
                                overflow: "hidden",
                            }}
                        >
                            <Typography variant="subtitle1" fontWeight="bold">
                                {t("paymentSchedules.total")}: {formatDecimalNumber(total)}%
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>{t("common.cancel")}</Button>
                <Button
                    onClick={handleSave}
                    variant="contained"
                    disabled={createMutation.isPending || updateMutation.isPending}
                >
                    {t("common.save")}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
